import { DownOutlineIcon } from '@carboncopyco/icons';
import type { Color, IconProps } from '@carboncopyco/icons';
import classNames from 'classnames';
import React, { useRef } from 'react';
import type { BgColors, Colors } from '../../constants';
import { TooltipBubble } from '../TooltipBubble';
import { TextBoxEditorTitle, Typography } from '../Typography';

export type DropdownCapsuleState = 'default' | 'open' | 'disabled';

type DropdownCapsuleSize = 'small' | 'medium';

const dropdownCapsulePadding: Record<DropdownCapsuleSize, string> = {
  small: 'py-1 px-3',
  medium: 'py-2 px-3',
};

const dropdownCapsuleOutline: Record<DropdownCapsuleState, string> = {
  default: 'outline outline-ds-natural-150 outline-offset-[-1px] outline-1',
  open: 'outline outline-ds-primary-300 outline-offset-[-1px] outline-1',
  disabled: 'outline outline-ds-natural-150 outline-offset-[-1px] outline-1',
};

const dropdownCapsuleHexColor: Record<DropdownCapsuleState, Color> = {
  default: '#0078EF',
  open: '#0078EF',
  disabled: '#E4E3E7',
};

const dropdownCapsuleValueTextColor: Record<DropdownCapsuleState, Colors> = {
  default: 'text-ds-natural-600',
  open: 'text-ds-natural-600',
  disabled: 'text-ds-natural-200',
};

const dropdownCapsulePlaceholderTextColor: Record<
  DropdownCapsuleState,
  Colors
> = {
  default: 'text-ds-natural-300',
  open: 'text-ds-natural-300',
  disabled: 'text-ds-natural-200',
};

const dropdownCapsuleCursor: Record<DropdownCapsuleState, string> = {
  default: '',
  open: '',
  disabled: '',
};
const dropdownCapsuleBackground: Record<DropdownCapsuleState, BgColors> = {
  default: 'bg-ds-accent3-100',
  open: 'bg-ds-accent3-100',
  disabled: 'bg-ds-accent3-100',
};

export type FilterCapsuleProps = {
  label?: string;
  placeholder: string;
  onClick?: (event: React.MouseEvent) => void;
  capsuleState?: DropdownCapsuleState;
  size?: DropdownCapsuleSize;
  rounded?: boolean;
  width?: number | 'full';
} & (
  | {
      contentWrapper: React.ReactElement;
      leftIcon?: never;
    }
  | {
      contentWrapper?: never;
      leftIcon?: React.FC<IconProps>;
    }
) &
  (
    | { multiSelect?: true; numberOfSelected: number }
    | { multiSelect?: false; numberOfSelected?: never }
  );

export const FilterCapsule = ({
  label,
  placeholder,
  multiSelect,
  numberOfSelected,
  onClick,
  capsuleState = 'default',
  size = 'small',
  rounded = true,
  leftIcon,
  contentWrapper,
  width,
}: FilterCapsuleProps) => {
  const capsuleRef = useRef<HTMLDivElement>();
  const LeftIcon = leftIcon || ((_props: IconProps) => null);
  const itemText = (
    <Typography
      color={
        (!multiSelect && !label) || (multiSelect && !numberOfSelected)
          ? dropdownCapsulePlaceholderTextColor[capsuleState]
          : dropdownCapsuleValueTextColor[capsuleState]
      }
      label={label || placeholder}
      ellipsis
    />
  );
  return (
    <>
      <TooltipBubble
        placement="top"
        title={label}
        disabled={!label || capsuleState === 'disabled'}
        anchorRef={capsuleRef}
      />
      <div
        ref={capsuleRef}
        onClick={onClick}
        style={{
          width: width ? (width === 'full' ? '100%' : `${width}px`) : 'initial',
        }}
        className={classNames(
          'group',
          rounded ? 'rounded-full' : 'rounded-md',
          dropdownCapsuleOutline[capsuleState],
          dropdownCapsulePadding[size],
          dropdownCapsuleCursor[capsuleState],
          dropdownCapsuleBackground[capsuleState],
          {
            'hover:outline-ds-natural-300': capsuleState === 'default',
          }
        )}
      >
        <div className="flex justify-between items-center">
          <div className="flex-auto overflow-x-hidden">
            {contentWrapper ? (
              React.cloneElement(contentWrapper, [], itemText)
            ) : (
              <div className="flex flex-auto items-center truncate">
                {leftIcon && (
                  <div className="shrink-0">
                    <LeftIcon
                      color={dropdownCapsuleHexColor[capsuleState]}
                      size={20}
                    />
                  </div>
                )}
                <div className="px-1 flex-auto flex justify-start items-center gap-2 overflow-x-hidden">
                  {itemText}
                  {!!numberOfSelected && numberOfSelected > 1 && (
                    <div className="flex justify-center items-center bg-ds-natural-100 text-ds-primary rounded-full px-1.5">
                      <TextBoxEditorTitle
                        color={
                          capsuleState === 'disabled'
                            ? 'text-ds-natural-200'
                            : 'text-ds-primary'
                        }
                        label={`+${(numberOfSelected - 1).toString()}`}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="shrink-0">
            <DownOutlineIcon
              size={20}
              color={capsuleState === 'disabled' ? '#E4E3E7' : '#0078EF'}
            />
          </div>
        </div>
      </div>
    </>
  );
};
